import React, { useState } from 'react';
import { SimulatorModel } from '../../../models/SimulatorModel';
import { SimulatorSettingsModel as SimulatorSettingsDto } from '../../../models/superbit-api-models';
import {
    DISCOUNT_COMPANY_TABS,
    DISCOUNT_COLUMNS,
    EMPTY_GUID,
} from '../discounts.config';
import DiscountForm from './DiscountForm';
import './DiscountsSettings.css';

interface DiscountsSettingsProps {
    api: SimulatorModel;
    settings: SimulatorSettingsDto;
}

type Feedback = { ok: boolean; msg: string } | null;

/**
 * "הנחות" tab — per-company discounts list with create / edit / delete.
 * List data is the loaded settings.discountsList; add/edit loads a template via
 * getDiscountForEdit and saves via upsert (returns refreshed settings).
 */
const DiscountsSettings: React.FC<DiscountsSettingsProps> = ({ api, settings }) => {
    const [activeCompany, setActiveCompany] = useState<string>(DISCOUNT_COMPANY_TABS[0].key);
    const [discounts, setDiscounts] = useState<any[]>(settings.discountsList ?? []);
    const [feedback, setFeedback] = useState<Feedback>(null);
    const [busyId, setBusyId] = useState<string | null>(null);

    const [formOpen, setFormOpen] = useState(false);
    const [formModel, setFormModel] = useState<any | null>(null);
    const [loadingForm, setLoadingForm] = useState(false);
    const [savingForm, setSavingForm] = useState(false);

    const tab = DISCOUNT_COMPANY_TABS.find((t) => t.key === activeCompany)!;
    const companyId = tab.id.toLowerCase();
    const columns = DISCOUNT_COLUMNS[companyId] ?? [];

    const rows = discounts.filter(
        (d) => (d.insuranceCompanyId ?? '').toString().toLowerCase() === companyId
    );

    const openForm = async (discountId: string) => {
        setFeedback(null);
        setFormOpen(true);
        setFormModel(null);
        setLoadingForm(true);
        try {
            const m = await api.simulatorGetDiscountForEdit(discountId, tab.id);
            setFormModel(m);
        } catch {
            setFeedback({ ok: false, msg: 'טעינת טופס ההנחה נכשלה' });
            setFormOpen(false);
        } finally {
            setLoadingForm(false);
        }
    };

    const closeForm = () => {
        setFormOpen(false);
        setFormModel(null);
    };

    const saveForm = async (payload: any) => {
        setSavingForm(true);
        try {
            const updated = await api.simulatorUpsertDiscount(payload);
            if (updated?.discountsList) setDiscounts(updated.discountsList);
            setFeedback({ ok: true, msg: 'ההנחה נשמרה' });
            closeForm();
        } catch {
            setFeedback({ ok: false, msg: 'שמירת ההנחה נכשלה' });
        } finally {
            setSavingForm(false);
        }
    };

    const deleteDiscount = async (d: any) => {
        // eslint-disable-next-line no-restricted-globals, no-alert
        if (!window.confirm(`למחוק את ההנחה "${d.name ?? ''}"?`)) return;
        setFeedback(null);
        setBusyId(d.id);
        try {
            await api.simulatorDeleteDiscount(d.id);
            setDiscounts((list) => list.filter((x) => x.id !== d.id));
            setFeedback({ ok: true, msg: 'ההנחה נמחקה' });
        } catch {
            setFeedback({ ok: false, msg: 'מחיקת ההנחה נכשלה' });
        } finally {
            setBusyId(null);
        }
    };

    if (formOpen) {
        return (
            <div className="sbsim-discounts">
                {loadingForm || !formModel ? (
                    <div className="sbsim__state">טוען טופס...</div>
                ) : (
                    <DiscountForm
                        companyId={companyId}
                        model={formModel}
                        saving={savingForm}
                        onSave={saveForm}
                        onCancel={closeForm}
                    />
                )}
            </div>
        );
    }

    return (
        <div className="sbsim-discounts">
            <nav className="sbsim-discounts__tabs">
                {DISCOUNT_COMPANY_TABS.map((c) => (
                    <button
                        key={c.key}
                        type="button"
                        className={`sbsim-discounts__tab ${
                            c.key === activeCompany ? 'sbsim-discounts__tab--active' : ''
                        }`}
                        onClick={() => setActiveCompany(c.key)}
                    >
                        {c.label}
                    </button>
                ))}
            </nav>

            <div className="sbsim-discounts__toolbar">
                <button
                    type="button"
                    className="sbsim-discounts__add"
                    onClick={() => openForm(EMPTY_GUID)}
                >
                    + הוסף הנחה
                </button>
                {feedback && (
                    <span
                        className={`sbsim-discounts__feedback ${
                            feedback.ok
                                ? 'sbsim-discounts__feedback--ok'
                                : 'sbsim-discounts__feedback--err'
                        }`}
                    >
                        {feedback.msg}
                    </span>
                )}
            </div>

            <div className="sbsim-discounts__scroll">
                <table className="sbsim-discounts__grid">
                    <thead>
                        <tr>
                            {columns.map((c) => (
                                <th key={c.label}>{c.label}</th>
                            ))}
                            <th className="sbsim-discounts__actions-col" />
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 && (
                            <tr>
                                <td className="sbsim-discounts__empty" colSpan={columns.length + 1}>
                                    אין הנחות
                                </td>
                            </tr>
                        )}
                        {rows.map((d) => (
                            <tr key={d.id}>
                                {columns.map((c) => (
                                    <td key={c.label}>{c.render(d)}</td>
                                ))}
                                <td className="sbsim-discounts__actions">
                                    <button
                                        type="button"
                                        className="sbsim-discounts__icon"
                                        title="עריכה"
                                        onClick={() => openForm(d.id)}
                                    >
                                        ✎
                                    </button>
                                    <button
                                        type="button"
                                        className="sbsim-discounts__icon sbsim-discounts__icon--danger"
                                        title="מחק"
                                        disabled={busyId === d.id}
                                        onClick={() => deleteDiscount(d)}
                                    >
                                        ✕
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default DiscountsSettings;
